import React from "react";
import { useContext } from "react";
import { NetworkStatus } from "@apollo/client";
import { ColumnDef } from "@tanstack/react-table";
import { useParams } from "react-router";
import { buildLink } from "common/routing";
import {
  QueryDescription,
  sourceForGroupId,
  TABLE_NAMES,
} from "common/utils/queryBuilder";
import { CustomObjectGroup } from "common-client/generated/graphql";
import {
  GetQueryTableDataQueryVariables,
  useGetQueryTableDataLazyQuery,
  useGetSavedViewsQuery,
  useGetTableConfigQuery,
} from "../../generated/graphql";
import { AuthContext } from "../Authorization/AuthContext";
import { someActionsEnabled } from "../Common/ActionBar/utils";
import { ACTION_COLUMN_DEF_CONSTANTS, ActionCell } from "../Common/ActionCell";
import { Layout } from "../Common/FullWidthLayout";
import { filesCell, TasksCell } from "../Common/FullWidthTable/customCells";
import { FullWidthTable } from "../Common/FullWidthTable/FullWidthTable";
import { Attribute } from "../Common/FullWidthTable/types";
import {
  initializeColumns,
  mergeFieldGroups,
  MinimalColumnDef,
} from "../Common/FullWidthTable/utils";
import {
  useManualPaginationConfig,
  useQueryDescription,
} from "../Common/Tables/hooks";
import {
  buildColumnDefinitions,
  CellRenderConfig,
} from "../Common/Tables/utils";
import ExportDataButton from "../Exports/ExportDataButton";
import { ActionsProps } from "../Inputs/DropdownMenu";

export const OBJECTS_TABLE_CUSTOM_CELLS: Record<string, CellRenderConfig> = {
  "CustomMapGeometries.files": {
    cell: ({ row }) => {
      return filesCell({ row: row.original["CustomMapGeometries.files"] });
    },
  },
  ["CustomMapGeometries.tasks"]: {
    cell: ({ row }) => {
      const tasks =
        row.original[`${TABLE_NAMES.CUSTOM_MAP_GEOMETRIES}.tasks`] || [];
      return <TasksCell tasks={tasks} />;
    },
  },
};

export const OBJECTS_TABLE_REQUIRED_FIELDS = [
  { table: TABLE_NAMES.CUSTOM_MAP_GEOMETRIES, name: "id" },
  { table: TABLE_NAMES.CUSTOM_MAP_GEOMETRIES, name: "customMapId" },
] as const;

const ObjectActions = ({
  objectData,
}: {
  objectData: {
    "CustomMapGeometries.id": string;
    "CustomMapGeometries.customMapId": string;
  };
}) => {
  const id = objectData["CustomMapGeometries.id"];
  const customMapId = objectData["CustomMapGeometries.customMapId"];

  const { account } = useContext(AuthContext);

  const actions: Array<ActionsProps> = [
    {
      label: "View on map",
      href: buildLink(
        "map",
        { accountId: account?.id! },
        {
          customMapId: customMapId,
          geometryId: id,
        },
      ),
      target: "_blank",
    },
  ];

  return <ActionCell actions={actions} />;
};

const ObjectsTable = ({ group }: { group: CustomObjectGroup }) => {
  const tableActions = [<ExportDataButton key={"exportData"} />];
  const actionsColumn: MinimalColumnDef<typeof OBJECTS_TABLE_REQUIRED_FIELDS> =
    {
      ...ACTION_COLUMN_DEF_CONSTANTS,
      cell: ({ row }) => {
        const object = row.original;
        return <ObjectActions objectData={object} />;
      },
    };

  const tableSource = sourceForGroupId(group.id);

  const { data: columnConfigResponse, loading: loadingColumnConfig } =
    useGetTableConfigQuery({
      variables: tableSource,
      fetchPolicy: "network-only",
    });

  const {
    data: savedViewsResponse,
    loading: loadingSavedViews,
    refetch: refetchSavedViews,
  } = useGetSavedViewsQuery({
    variables: tableSource,
    fetchPolicy: "cache-and-network",
  });

  const [
    queryTableData,
    { previousData, data: currentData, networkStatus, loading, error, refetch },
  ] = useGetQueryTableDataLazyQuery({
    fetchPolicy: "network-only",
    errorPolicy: "all",
  });

  const search = ({ page }: { page: number }) => {
    const variables: GetQueryTableDataQueryVariables = {
      description: {
        ...queryDescription,
        fields: mergeFieldGroups(
          queryDescription.fields,
          OBJECTS_TABLE_REQUIRED_FIELDS,
        ),
      } satisfies QueryDescription,
      page,
    };

    void queryTableData({
      variables,
    });
  };

  const {
    initialTableState,
    queryDescription,
    updateQueryDescription,
    currentView,
  } = useQueryDescription({
    source: tableSource,
    defaultSort: [{ id: "CustomMapGeometries.label", desc: false }],
    savedViews: savedViewsResponse?.account?.savedViews ?? [],
  });

  const manualPaginationConfig = useManualPaginationConfig({
    ...initialTableState.pagination,
    currentTotalPages: currentData?.queryTableData.pageInfo.totalPages,
    previousTotalPages: previousData?.queryTableData.pageInfo.totalPages,
  });

  const generatedColumnDefinitions = buildColumnDefinitions({
    columnConfig: columnConfigResponse?.getTableConfig.data ?? [],
    cellRenderers: OBJECTS_TABLE_CUSTOM_CELLS,
  });

  const tanstackColumnDefinitions: Array<ColumnDef<any>> = [
    ...generatedColumnDefinitions,
  ];

  const actionBarConfig = columnConfigResponse?.getTableConfig.actionBarConfig;
  const enableSelectColumn = someActionsEnabled(actionBarConfig?.actions ?? {});

  const initialColumns = initializeColumns({
    initialTableState,
    tanstackColumnDefinitions,
    initialQueryDescription: queryDescription,
    actionsColumn,
    enableSelectColumn,
  });

  const loadingDetails = {
    loading,
    loadingText: `Loading ${group.name} objects`,
    noDataText: `No ${group.name} objects found`,
  };

  const data =
    networkStatus === NetworkStatus.setVariables ? previousData : currentData;

  const timeoutError = error?.graphQLErrors.find(
    e => e.extensions.code === "TIMEOUT_ERROR",
  );

  if (
    loadingColumnConfig ||
    !columnConfigResponse ||
    loadingSavedViews ||
    !currentView
  ) {
    return null;
  }

  return (
    <FullWidthTable
      columns={initialColumns}
      initialState={initialTableState}
      manualPaginationConfig={{
        ...manualPaginationConfig,
        pageCount: data?.queryTableData.pageInfo.totalPages ?? 1,
      }}
      actions={tableActions}
      currentData={data?.queryTableData.data ?? []}
      queryDescription={queryDescription}
      updateQueryDescription={updateQueryDescription}
      loadingDetails={loadingDetails}
      timeoutError={timeoutError}
      currentView={currentView}
      savedViews={savedViewsResponse?.account?.savedViews}
      refetchSavedViews={refetchSavedViews}
      refetchTable={refetch}
      filterable={{
        filterConfiguration: columnConfigResponse.getTableConfig
          .data as Array<Attribute>,
        search,
      }}
      columnSettingProps={{
        columnConfiguration: columnConfigResponse.getTableConfig.data,
        columnDefinitions: tanstackColumnDefinitions,
      }}
      clientSideColumnConfig={columnConfigResponse?.getTableConfig.data}
      dataSourceComputationsAffectingQuery={
        data?.queryTableData.dataSourceComputationsAffectingQuery
      }
      invalidQueryConfig={data?.queryTableData.invalidQueryConfig ?? null}
      rowIdField={`${TABLE_NAMES.CUSTOM_MAP_GEOMETRIES}.id`}
      totalCount={data?.queryTableData.pageInfo.totalCount ?? 0}
      actionBarConfig={actionBarConfig}
    />
  );
};

const ObjectsTableWrapper = () => {
  const { account } = useContext(AuthContext);
  const groupId = useParams<{ groupId: string }>().groupId;
  const group = account?.customObjectGroups.find(group => group.id === groupId);

  if (!group) {
    return <div>Group not found</div>;
  }

  const title = group.name;

  return (
    <Layout title={`${title}`}>
      <ObjectsTable group={group} />
    </Layout>
  );
};

export default ObjectsTableWrapper;
